import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useMyOrders } from '@/hooks/useMyOrders';
import { useOrderDetail } from '@/hooks/useOrderDetail';
import {
  useMySubscriptionHistory,
  type SubscriptionHistoryItem,
} from '@/hooks/useMySubscriptionHistory';
import { QrTicket } from '@/components/QrTicket';
import {
  Badge,
  Button,
  Card,
  CardTitle,
  Modal,
  Pagination,
  Skeleton,
  SkeletonGroup,
  type BadgeTone,
} from '@/components/ui';
import { PageBanner } from '@/components/ui/PageBanner';
import { useConcertBackdrop } from '@/hooks/useConcertBackdrop';
import { formatCOP, formatEventDate } from '@/lib/format';
import type { OrderSummary } from '@/lib/checkoutClient';
import { messageForCode } from '@/lib/checkout/messages';
import { cycleLabel } from '@/lib/membershipLifecycle';

const PAGE_SIZE = 6;

type StatusFilter = 'all' | 'active' | 'used' | 'cancelled';

const STATUS_GROUPS: Record<StatusFilter, ReadonlyArray<string>> = {
  all: [],
  active: ['confirmed', 'qr_issued', 'captured'],
  used: ['used'],
  cancelled: ['cancelled', 'expired', 'failed', 'refunded', 'partially_refunded', 'disputed'],
};

/**
 * Estados de orden en los que tiene sentido hidratar el detalle
 * (`GET /v1/orders/:id`) para mostrar la cantidad de items y los QRs: el
 * listado `/orders/mine` no los trae. Para órdenes que nunca emitieron boletas
 * (canceladas/expiradas/fallidas) evitamos el fetch extra.
 */
const ORDER_DETAIL_STATUSES = new Set(['captured', 'confirmed', 'qr_issued', 'used']);

function statusTone(status: string): BadgeTone {
  if (status === 'confirmed' || status === 'qr_issued') return 'success';
  if (status === 'used') return 'gold';
  if (status === 'captured' || status === 'payment_pending' || status === 'cart_locked')
    return 'info';
  if (status === 'cancelled' || status === 'expired' || status === 'failed') return 'danger';
  if (status === 'refunded' || status === 'partially_refunded' || status === 'disputed')
    return 'warning';
  return 'neutral';
}

/**
 * MyTickets — TASK-C09 (PRIORIDAD 6).
 *
 * GET /order/mine (RLS-filtered). Cards por order with status badge +
 * total + items count + "Ver QR" → Modal con el QR full-size.
 *
 * Filter por status (all/active/used/cancelled) — chip tabs.
 * Pagination 6 por página (PAGE_SIZE).
 * Sort: createdAt DESC (server-side responsibility; ADDENDUM confirma).
 *
 * Auth gate: route-level via RequireAuth. Si el user no está autenticado,
 * lo bouncea a /login?from=/my-tickets antes de llegar aquí.
 */
export function MyTickets(): JSX.Element {
  const navigate = useNavigate();
  const { data: orders, isLoading, isError, error, refetch } = useMyOrders();
  // ROUND35 FC-022 — además de orders (boletas/productos), incluimos
  // historial de suscripciones para que las compras de membresía aparezcan
  // en este listado canónico. Se rinde en sección propia "Membresías".
  const { data: subscriptions, isLoading: subsLoading } = useMySubscriptionHistory();
  const [filter, setFilter] = useState<StatusFilter>('all');
  const [page, setPage] = useState(1);
  const [activeQrOrder, setActiveQrOrder] = useState<OrderSummary | null>(null);
  const heroImage = useConcertBackdrop();

  const filtered = useMemo(() => {
    if (!orders) return [];
    if (filter === 'all') return orders;
    const allowed = new Set(STATUS_GROUPS[filter]);
    return orders.filter((o) => allowed.has(o.status));
  }, [orders, filter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageOrders = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const subscriptionItems = subscriptions ?? [];
  const hasOrders = !!orders && orders.length > 0;
  const hasSubscriptions = subscriptionItems.length > 0;

  // ---- Loading (ambas queries en flight) ----
  if (isLoading || (!orders && !subscriptions && subsLoading)) {
    return (
      <section className="container-fc py-12">
        <h1 className="font-display text-3xl">Mis compras</h1>
        <SkeletonGroup
          label="Cargando boletas"
          className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3"
        >
          <Skeleton shape="h-40" />
          <Skeleton shape="h-40" />
          <Skeleton shape="h-40" />
        </SkeletonGroup>
      </section>
    );
  }

  // ---- Error ----
  if (isError) {
    const code = (error as { code?: string } | null)?.code;
    const status = (error as { status?: number } | null)?.status;
    return (
      <section className="container-fc py-12">
        <h1 className="font-display text-3xl">Mis compras</h1>
        <p className="mt-4 text-fc-muted">{messageForCode(code, status)}</p>
        <Button className="mt-4" variant="secondary" onClick={() => void refetch()}>
          Reintentar
        </Button>
      </section>
    );
  }

  // ---- Empty (sin orders Y sin suscripciones) ----
  if (!hasOrders && !hasSubscriptions) {
    return (
      <section className="container-fc py-12">
        <h1 className="font-display text-3xl">Mis compras</h1>
        <p className="mt-4 text-fc-muted">Todavía no tenés compras en tu cuenta.</p>
        <div className="mt-6 flex flex-wrap gap-2">
          <Button onClick={() => navigate('/')}>Ver eventos</Button>
          <Button variant="secondary" onClick={() => navigate('/memberships')}>
            Ver membresías
          </Button>
        </div>
      </section>
    );
  }

  return (
    <>
      <PageBanner
        eyebrow="Mi cuenta"
        title="Mis Tickets"
        subtitle="Tus boletas y membresías compradas, siempre a mano."
        heroImage={heroImage}
      />
      <section className="container-fc py-12 md:py-16">
        <div className="mb-6 flex justify-start md:justify-end">
          <FilterTabs
            value={filter}
            onChange={(v) => {
              setFilter(v);
              setPage(1);
            }}
          />
        </div>

        {/* ROUND35 FC-022 — Membresías adquiridas (subscription_history).
          Sección propia con badge "Membresía", arriba de las boletas. */}
        {hasSubscriptions && (
          <section aria-labelledby="memberships-section" className="mb-8">
            <h2 id="memberships-section" className="font-display text-xl text-fc-ink md:text-2xl">
              Membresías
            </h2>
            <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
              {subscriptionItems.map((sub) => (
                <SubscriptionCard key={sub.id} item={sub} />
              ))}
            </div>
          </section>
        )}

        {hasOrders && (
          <h2 className="mb-4 font-display text-xl text-fc-ink md:text-2xl">Boletas y compras</h2>
        )}

        {pageOrders.length === 0 && hasOrders ? (
          <Card variant="ghost">
            <p className="text-fc-muted">No tenés boletas en esta categoría.</p>
          </Card>
        ) : pageOrders.length === 0 ? null : (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
            {pageOrders.map((order) => (
              <OrderCard
                key={order.id}
                order={order}
                onViewQr={(hydrated) => setActiveQrOrder(hydrated)}
              />
            ))}
          </div>
        )}

        {totalPages > 1 && (
          <div className="mt-6 flex justify-center">
            <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />
          </div>
        )}

        <Modal
          open={!!activeQrOrder}
          onOpenChange={(open) => !open && setActiveQrOrder(null)}
          title={activeQrOrder ? `Boletas · ${activeQrOrder.orderNumber}` : 'Boletas'}
          size="lg"
        >
          {activeQrOrder ? (
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              {activeQrOrder.qrCodes.map((qr, idx) => (
                <div key={qr.id} className="flex flex-col items-center gap-2">
                  <QrTicket
                    code={qr.code}
                    label={
                      qr.seatLabel
                        ? `Asiento ${qr.seatLabel}`
                        : `Boleta ${idx + 1} de ${activeQrOrder.qrCodes.length}`
                    }
                    size={220}
                  />
                  {/* F5 — QR nominal: titular del asiento */}
                  {qr.holderName && (
                    <p className="text-sm font-medium text-fc-ink">{qr.holderName}</p>
                  )}
                  <p className="text-xs text-fc-muted">
                    Vence <time dateTime={qr.expiresAt}>{formatEventDate(qr.expiresAt)}</time>
                  </p>
                </div>
              ))}
              {activeQrOrder.qrCodes.length === 0 && (
                <p className="text-fc-muted">Esta orden no tiene QRs emitidos.</p>
              )}
            </div>
          ) : null}
        </Modal>
      </section>
    </>
  );
}

function FilterTabs({
  value,
  onChange,
}: {
  value: StatusFilter;
  onChange: (v: StatusFilter) => void;
}): JSX.Element {
  const opts: Array<{ id: StatusFilter; label: string }> = [
    { id: 'all', label: 'Todas' },
    { id: 'active', label: 'Activas' },
    { id: 'used', label: 'Usadas' },
    { id: 'cancelled', label: 'Canceladas' },
  ];
  return (
    <div role="tablist" aria-label="Filtrar boletas" className="flex flex-wrap gap-1">
      {opts.map((o) => {
        const active = o.id === value;
        return (
          <button
            key={o.id}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(o.id)}
            className={[
              'rounded-card px-3 py-1.5 text-sm transition-colors',
              'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-fc-gold',
              active
                ? 'bg-primary text-fc-bg'
                : 'border border-fc-line bg-fc-paper text-fc-ink hover:bg-fc-line/40',
            ].join(' ')}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

// R36 C2 — mapping de status raw (inglés) a label en español.
// Operator quote: "¿Por qué siguen apareciendo tags en inglés cuando la
// instrucción es que todo sea en español?". Status como 'superseded',
// 'active', 'canceled' venían directos de la DB.
// R37 C-2 — "superseded" se renombra a "Renovada" (el user no entendía
// "Reemplazada"; cuando se renueva la suscripción, la anterior queda
// superseded). "Vencida" sigue para `expired` (cuando no se renovó).
const SUBSCRIPTION_STATUS_LABEL_ES: Record<string, string> = {
  active: 'Activa',
  canceled: 'Cancelada',
  cancelled: 'Cancelada',
  expired: 'Vencida',
  superseded: 'Renovada',
  pending: 'Pendiente',
  paused: 'Pausada',
  unknown: 'Desconocido',
};

function subscriptionStatusLabelEs(raw: string): string {
  return SUBSCRIPTION_STATUS_LABEL_ES[(raw || '').toLowerCase()] || raw;
}

function SubscriptionCard({ item }: { item: SubscriptionHistoryItem }): JSX.Element {
  // ROUND35 FC-022 — render dedicado para una membresía adquirida.
  // Status normalizado: 'active' → success, 'canceled'/'expired' → neutral.
  const tone: BadgeTone =
    item.status === 'active'
      ? 'success'
      : item.status === 'canceled' || item.status === 'cancelled' || item.status === 'expired'
        ? 'neutral'
        : 'info';
  return (
    <Card variant="default" padded>
      <div className="flex items-start justify-between gap-2">
        <CardTitle>{item.planName}</CardTitle>
        <Badge tone="gold">Membresía</Badge>
      </div>
      <dl className="mt-3 grid grid-cols-1 gap-y-1 text-sm">
        <dt className="text-fc-muted">Ciclo</dt>
        <dd>{cycleLabel(item.cycle as 'monthly' | 'annual')}</dd>
        {item.amountCop !== null && (
          <>
            <dt className="mt-2 text-fc-muted">Monto</dt>
            <dd className="font-semibold tabular-nums">{formatCOP(item.amountCop)}</dd>
          </>
        )}
        {item.startedAt && (
          <>
            <dt className="mt-2 text-fc-muted">Inicio</dt>
            <dd>{formatEventDate(item.startedAt)}</dd>
          </>
        )}
        {item.endsAt && (
          <>
            <dt className="mt-2 text-fc-muted">Vence</dt>
            <dd>{formatEventDate(item.endsAt)}</dd>
          </>
        )}
        <dt className="mt-2 text-fc-muted">Estado</dt>
        <dd>
          <Badge tone={tone}>{subscriptionStatusLabelEs(item.status)}</Badge>
        </dd>
      </dl>
    </Card>
  );
}

function OrderCard({
  order,
  onViewQr,
}: {
  order: OrderSummary;
  onViewQr: (hydrated: OrderSummary) => void;
}): JSX.Element {
  // El listado `/orders/mine` no trae items ni QRs; los hidratamos del detalle
  // para los estados que sí los tienen. Mientras carga, usamos la orden del
  // listado (items/QRs vacíos) sin romper la card.
  const wantsDetail = ORDER_DETAIL_STATUSES.has(order.status);
  const detail = useOrderDetail(order.id, wantsDetail);
  const hydrated = detail.data ?? order;

  const totalQty = hydrated.items.reduce((sum, it) => sum + it.quantity, 0);
  const tone = statusTone(hydrated.status);
  const qrCount = hydrated.qrCodes.length;
  const showQrButton = qrCount > 0;
  // Mientras el detalle aún carga para una orden que debería tener QRs.
  const loadingDetail = wantsDetail && detail.isLoading;
  return (
    <Card variant="default" padded>
      <div className="flex items-start justify-between gap-2">
        <CardTitle>{hydrated.orderNumber}</CardTitle>
        <Badge tone={tone}>{hydrated.status}</Badge>
      </div>
      <dl className="mt-3 grid grid-cols-1 gap-y-1 text-sm">
        <dt className="text-fc-muted">Creada</dt>
        <dd>{formatEventDate(hydrated.createdAt)}</dd>
        <dt className="mt-2 text-fc-muted">Total</dt>
        <dd className="font-semibold tabular-nums">{formatCOP(hydrated.totalCents)}</dd>
        <dt className="mt-2 text-fc-muted">Items</dt>
        <dd>{loadingDetail && totalQty === 0 ? '…' : totalQty}</dd>
      </dl>
      <div className="mt-4 flex flex-col gap-2">
        <Button
          variant={showQrButton ? 'primary' : 'secondary'}
          size="sm"
          onClick={() => onViewQr(hydrated)}
          disabled={!showQrButton}
        >
          {loadingDetail
            ? 'Cargando boletas…'
            : showQrButton
              ? `Ver QR (${qrCount})`
              : 'Sin QR emitido'}
        </Button>
      </div>
    </Card>
  );
}
